import React from 'react';
import { SubHeading, Body } from '../Constants/Fonts';

// NoDataPage — the shared empty-state block: a layered circular badge, a title,
// a one-line hint, and an optional action (e.g. a "Create" button), centred in
// its container. Fully theme-driven (tokens + Fonts.js presets). Drop it in
// wherever a list/chart/table has nothing to show.
//
// It flex-grows to fill its parent (so inside an equal-height card it centres
// vertically); `minHeight` guarantees a sensible size when standing alone.
//
// Props:
//   title    — bold headline (default "No data yet")
//   hint     — muted supporting line
//   icon     — optional custom node, replaces the default glyph (kept inside the badge)
//   action   — optional node under the hint (a CTA button, link…)
//   minHeight— min box height (px, default 200)
//   style    — escape hatch merged onto the wrapper
export default function NoDataPage({ title = 'No data yet', hint, icon, action, minHeight = 200, style }) {
    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 6,
                padding: '32px 16px',
                minHeight,
                ...style,
            }}
        >
            {/* Layered badge: soft navy outer ring cradling a white inner disc. */}
            <span
                style={{
                    width: 68,
                    height: 68,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--color-primary-soft)',
                    marginBottom: 8,
                }}
            >
                <span
                    style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        boxShadow: 'var(--shadow-sm)',
                        color: 'var(--color-primary)',
                    }}
                >
                    {icon || (
                        // An empty inbox reads as "nothing here yet" better than an info glyph.
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M22 12h-6l-2 3h-4l-2-3H2" />
                            <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                        </svg>
                    )}
                </span>
            </span>

            <SubHeading variant={2} color="var(--color-text-primary)" style={{ margin: 0 }}>{title}</SubHeading>
            {hint && <Body variant={2} color="var(--color-text-muted)" style={{ margin: 0, maxWidth: 320, lineHeight: 1.5 }}>{hint}</Body>}
            {action && <div style={{ marginTop: 16 }}>{action}</div>}
        </div>
    );
}
